import { LitElement, css, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { PUBLIC_PTC_HELP_SITE } from "../env";
import { appMode } from "../init";
import { getSuggestedHelpPage } from "../lib/help-page";
import { getSingleton, singleton } from "../lib/singleton";
import { contactSupport } from "../vendor/helpscout";
import { Dialog } from "./dialog";
import { Popover } from "./popover";

@customElement("ptc-help")
export class HelpWidget extends Dialog<{ displaySupportButton: boolean } | undefined, void> {
    static helpSite = PUBLIC_PTC_HELP_SITE;

    static rootPage = "#search-input";

    readonly showScrim = false;

    @property({ type: Boolean })
    displaySupportButton = true;

    @state()
    private _page: string;

    @query("iframe")
    private _iframe: HTMLIFrameElement;

    async show({
        displaySupportButton = true,
        page = undefined,
    }: { displaySupportButton?: boolean; page?: string } = {}) {
        const promise = super.show();
        this.displaySupportButton = displaySupportButton;
        await this.updateComplete;
        this._openPage(page || getSuggestedHelpPage());
        return promise;
    }

    private _openPage(page = HelpWidget.rootPage) {
        this._page = page;
        this._iframe.src = `${HelpWidget.helpSite}${page}`;
    }

    private _openPageInNewTab(page = this._page) {
        window.open(`${HelpWidget.helpSite}${page}`, "Pentacode Hilfe");
    }

    private _contactSupport() {
        this.done();
        contactSupport();
    }

    static styles = [
        ...Dialog.styles,
        css`
            :host {
                z-index: 1000;
            }

            .outer {
                align-items: flex-start;
                justify-content: flex-end;
                padding: 2.5em;
                transform-origin: bottom left;
            }

            .inner {
                height: 40em;
                width: 25em;
                max-height: 100%;
                max-width: 100%;
                display: flex;
                flex-direction: column;
            }

            .article {
                padding: 0.5em;
                text-decoration: none;
                margin: 0 0.5em;
            }

            .article:not(:first-child) {
                border-top: solid 1px var(--shade-2);
            }

            .article:hover,
            .article:hover + .article {
                border-radius: var(--border-radius);
                border-color: transparent;
            }

            @media (max-width: 420px) {
                .outer {
                    padding: 0;
                }

                .inner {
                    box-shadow: none;
                    border-radius: 0;
                    height: 100%;
                    width: 100%;
                }
            }
        `,
    ];

    renderContent() {
        return html`
            <div class="padded horizontal center-aligning layout border-bottom">
                <div class="stretch"><ptc-ui-icon name="icon.action.help"></ptc-ui-icon> Hilfe</div>
                <button class="transparent icon" @click=${() => this._openPage(HelpWidget.rootPage)} tabindex="-1">
                    <ptc-ui-icon name="icon.action.search"></ptc-ui-icon>
                </button>
                <button class="transparent icon" @click=${() => this.done()}>
                    <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                </button>
            </div>
            <div class="padded" hidden>
                <div class="left icon input">
                    <ptc-ui-icon name="icon.action.search"></ptc-ui-icon>
                </div>
            </div>
            <iframe class="stretch"></iframe>
            <div
                class="smaller padded horizontal evenly stretching spacing layout"
                ?hidden=${!this.displaySupportButton}
            >
                <button class="subtle" @click=${() => this._openPageInNewTab()}>
                    <ptc-ui-icon name="icon.action.open-in-tab"></ptc-ui-icon>
                    In Neuem Tab Öffnen
                </button>
                ${
                    // Live support runs on the Help Scout beacon, which is
                    // never initialized in embedded mode (see initApp) —
                    // the button would silently no-op there (PTC-3919).
                    appMode !== "embedded"
                        ? html`
                              <button class="subtle" @click=${this._contactSupport}>
                                  <ptc-ui-icon name="icon.action.contact-support"></ptc-ui-icon>
                                  Support Kontaktieren
                              </button>
                          `
                        : ""
                }
            </div>
        `;
    }
}

@customElement("ptc-help-popover")
export class HelpPopover extends Popover {
    @property()
    page: string = "";

    private get _url() {
        return `${PUBLIC_PTC_HELP_SITE}${this.page}`;
    }

    static styles = [
        ...Popover.styles,
        css`
            :host {
                z-index: 1000;
                width: 300px;
                height: 400px;
            }
        `,
    ];

    private _openInHelpWidget() {
        openHelp({ page: this.page });
        this.hide();
    }

    private _openInBrowser() {
        window.open(this._url, "Pentacode Hilfe");
        this.hide();
    }

    renderContent() {
        return html`
            <div class="fullbleed vertical layout">
                <iframe class="stretch" .src=${this._url}> </iframe>
                <div class="tiny padded horizontal layout">
                    <button class="subtle" @click=${this._openInHelpWidget}>
                        <ptc-ui-icon name="icon.navigation.back" class="smaller"></ptc-ui-icon>
                        <ptc-ui-icon name="icon.action.open-in-window"></ptc-ui-icon> Im Hilfefenster Öffnen
                    </button>
                    <div class="stretch"></div>
                    <button class="subtle" @click=${this._openInBrowser}>
                        <ptc-ui-icon name="icon.action.open-in-tab"></ptc-ui-icon> In Neuem Tab Öffnen
                        <ptc-ui-icon name="icon.navigation.forward" class="smaller"></ptc-ui-icon>
                    </button>
                </div>
            </div>
        `;
    }
}

@customElement("ptc-help-anchor")
export class HelpAnchor extends LitElement {
    @property()
    page: string;

    @singleton("ptc-help-popover")
    private _popover: HelpPopover;

    private _hidePopoverTimeout?: number;

    private _leaveAnchorhandler = () => (this._hidePopoverTimeout = window.setTimeout(() => this._hidePopover(), 100));
    private _enterPopoverHandler = () => window.clearTimeout(this._hidePopoverTimeout);

    private _setup() {
        this.addEventListener("mouseleave", this._leaveAnchorhandler);
        this._popover.addEventListener("mouseenter", this._enterPopoverHandler);
        this._popover.addEventListener("popover-hide", () => this._tearDown(), { once: true });
    }

    private _tearDown() {
        this.removeEventListener("mouseleave", this._leaveAnchorhandler);
        this._popover.removeEventListener("mouseenter", this._enterPopoverHandler);
        this._popover.page = "";
    }

    private _showPopover() {
        this._setup();
        this._popover.page = this.page;
        this._popover.hideOnLeave = true;
        void this._popover.showAt(this);
    }

    private _hidePopover() {
        this._popover.hide();
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("mouseenter", () => this._showPopover());
    }

    render() {
        return html`<slot></slot>`;
    }
}

export function openHelp(params?: { displaySupportButton?: boolean; page?: string }) {
    const widget = getSingleton("ptc-help") as HelpWidget;
    void widget.show(params);
}
